import { useIntl } from 'react-intl';

import { WLIDS } from 'Constants';
import { store } from 'Models/Store';
import { WidthDto } from 'Models/Entities/WidthEntity';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { SelectInput } from 'Views/Components/New/SelectInput';

interface CargoTypeForWidthInputDto {
	id: string;
	minimumWidthId?: string;
}

interface VehicleWidthInputProps {
	cargoTypes: CargoTypeForWidthInputDto[];
	cargoTypeId?: string;
	vehicleWidthId?: string;
	onChange: (vehicleWidthId: string) => void;
	prices?: { [id: string]: number };
	isRequired?: boolean;
	errors?: string;
	/**
	 * When the vehicle and trailer widths are linked, this single picker drives both. Pass the
	 * trailer tow-on type's minimum width id here so widths below the trailer's minimum are also
	 * disabled — otherwise a wider-than-standard trailer could be given a too-narrow width.
	 */
	additionalMinimumWidthId?: string;
}

export default function VehicleWidthInput({
	cargoTypes,
	cargoTypeId,
	vehicleWidthId,
	onChange,
	prices,
	isRequired,
	errors,
	additionalMinimumWidthId,
}: VehicleWidthInputProps) {
	const intl = useIntl();
	const label = intl.formatMessage({ id: WLIDS.width.dropdownLabel, defaultMessage: 'Vehicle width' });
	const placeholder = intl.formatMessage({ id: WLIDS.width.dropdownPlaceholder, defaultMessage: 'Select width' });

	const options = processWidthOptions({
		cargoTypeId,
		widths: store.widths,
		cargoTypes,
		prices,
		additionalMinimumWidthId,
	});

	const selectedOption = options.find(o => o.value === vehicleWidthId) ?? null;

	return (
		<SelectInput
			className="vehicle-width-input"
			label={label}
			placeholder={placeholder}
			options={options}
			value={selectedOption}
			required={isRequired}
			isSearchable={false}
			error={errors}
			onChange={(option: any) => {
				onChange(option?.value ?? '');
			}}
		/>
	);
}

interface ProcessWidthOptionsArgs {
	cargoTypeId?: string;
	widths: WidthDto[];
	cargoTypes: CargoTypeForWidthInputDto[];
	prices?: { [id: string]: number };
	additionalMinimumWidthId?: string;
}

const processWidthOptions = ({
	cargoTypeId,
	widths,
	cargoTypes,
	prices,
	additionalMinimumWidthId,
}: ProcessWidthOptionsArgs) => {
	const baseOptions = widths.map(x => ({ label: x.label, value: x.id, isDisabled: false }));

	if (prices) {
		for (const option of baseOptions) {
			const price = prices[option.value];
			if (price && price !== 0) {
				const priceLabel = formatPriceDisplay(price, false, true, true);
				option.label = `${option.label} (${priceLabel})`;
			}
		}
	}

	const cargoType = cargoTypes.find(v => v.id === cargoTypeId);

	const baseWidth = store.widths.find(w => w.isBase);

	// The effective minimum is the widest of the cargo type's minimum and any additional (linked
	// trailer) minimum, so the picker enforces whichever requires the most width.
	const candidateMinimumIds = [cargoType?.minimumWidthId, additionalMinimumWidthId];
	const minimumWidth = candidateMinimumIds
		.map(id => widths.find(w => w.id === id))
		.filter((w): w is WidthDto => !!w)
		.reduce<WidthDto | undefined>(
			(widest, w) => (!widest || w.capacityMultiplier > widest.capacityMultiplier ? w : widest),
			undefined,
		);

	if (!baseWidth || !minimumWidth) {
		return baseOptions;
	}

	const invalidWidthIds = new Set(
		widths.filter(x => x.capacityMultiplier < minimumWidth.capacityMultiplier).map(x => x.id),
	);

	return baseOptions.map(x => ({ ...x, isDisabled: invalidWidthIds.has(x.value) }));
};
